import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  CLOCK_RESYNC_INTERVAL_MS,
  CLOCK_SAMPLE_COUNT,
  estimateClockOffset,
  type ClientToServerEvents,
  type ServerToClientEvents,
} from '@srcbattle/shared';

export interface ClockState {
  connected: boolean;
  offsetMs: number;
  rttMs: number;
  synced: boolean;
  /** The server's clock, as best this client can estimate it. */
  serverNow: () => number;
}

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Keeps this client's estimate of server time current.
 *
 * Everything about synchronised playback rests on this: the server broadcasts
 * an anchor in its own clock, and each client works out where it should be by
 * comparing against that clock. An offset that is wrong by 300ms means audio
 * that is wrong by 300ms, so the handshake runs on connect and then keeps
 * running to track drift.
 */
export function useClock(enabled: boolean): ClockState {
  const socketRef = useRef<AppSocket | null>(null);
  const offsetRef = useRef(0);
  const [state, setState] = useState({
    connected: false,
    offsetMs: 0,
    rttMs: 0,
    synced: false,
  });

  useEffect(() => {
    if (!enabled) return;

    const socket: AppSocket = io({ withCredentials: true });
    socketRef.current = socket;

    let disposed = false;

    async function runHandshake() {
      const samples: Array<{ t0: number; tServer: number; t2: number }> = [];

      for (let i = 0; i < CLOCK_SAMPLE_COUNT; i++) {
        if (disposed || !socket.connected) return;

        const sample = await new Promise<{ t0: number; tServer: number; t2: number } | null>(
          (resolve) => {
            const t0 = Date.now();
            // A lost ack must not stall the handshake forever.
            const timeout = setTimeout(() => resolve(null), 2000);

            socket.emit('clock:ping', { t0 }, (pong) => {
              clearTimeout(timeout);
              resolve({ t0: pong.t0, tServer: pong.tServer, t2: Date.now() });
            });
          },
        );

        if (sample) samples.push(sample);
        // Spacing the samples out stops a single congested moment from
        // poisoning all ten of them at once.
        await new Promise((resolve) => setTimeout(resolve, 40));
      }

      if (disposed || samples.length === 0) return;

      const estimate = estimateClockOffset(samples);
      offsetRef.current = estimate.offsetMs;
      setState((previous) => ({
        ...previous,
        offsetMs: estimate.offsetMs,
        rttMs: estimate.rttMs,
        synced: true,
      }));
    }

    socket.on('connect', () => {
      setState((previous) => ({ ...previous, connected: true }));
      void runHandshake();
    });

    socket.on('disconnect', () => {
      setState((previous) => ({ ...previous, connected: false, synced: false }));
    });

    const resyncTimer = setInterval(() => void runHandshake(), CLOCK_RESYNC_INTERVAL_MS);

    return () => {
      disposed = true;
      clearInterval(resyncTimer);
      socket.close();
      socketRef.current = null;
    };
  }, [enabled]);

  return {
    ...state,
    serverNow: () => Date.now() + offsetRef.current,
  };
}
